import { isHighNotHelpful } from "@/pages/help/faqData";
import { hasLiveOverride, type FaqContentOverride } from "@/pages/help/faqContent";
import type { FaqFeedbackRow } from "@/pages/admin/help/hooks/useFaqFeedback";
import type { FaqOptimizationRow } from "@/pages/admin/help/hooks/useFaqOptimization";

/** 按问题维度聚合后的统计行，供反馈汇总页与周报共用。 */
export interface FaqStatRow {
  qKey: string;
  category: string;
  helpful: number;
  notHelpful: number;
  total: number;
  rate: number;
  lastAt: string | null;
  needsOptimize: boolean;
  resolved: boolean;
  note: string | null;
  owner: string | null;
  resolvedBy: string | null;
  edited: boolean;
}

/**
 * 把原始反馈按问题聚合，并合并「已完善」状态、内容覆盖与「待优化」判定。
 * 汇总表与负责人工作量周报都基于这份结果，保证口径一致。
 */
export function buildFaqStats(
  rows: FaqFeedbackRow[],
  optimizations: Record<string, FaqOptimizationRow>,
  overrides: Record<string, FaqContentOverride>,
  rateThreshold: number,
  minNotHelpful: number,
): FaqStatRow[] {
  const map = new Map<string, FaqStatRow>();
  rows.forEach((row) => {
    let item = map.get(row.q_key);
    if (!item) {
      item = {
        qKey: row.q_key,
        category: row.category ?? "",
        helpful: 0,
        notHelpful: 0,
        total: 0,
        rate: 0,
        lastAt: null,
        needsOptimize: false,
        resolved: false,
        note: null,
        owner: null,
        resolvedBy: null,
        edited: false,
      };
      map.set(row.q_key, item);
    }
    if (row.helpful) item.helpful += 1;
    else item.notHelpful += 1;
    item.total += 1;
    if (!item.lastAt || row.created_at > item.lastAt) item.lastAt = row.created_at;
  });
  return Array.from(map.values()).map((item) => {
    const rate = item.total > 0 ? item.notHelpful / item.total : 0;
    const opt = optimizations[item.qKey];
    const resolved = Boolean(opt?.resolved);
    return {
      ...item,
      rate,
      resolved,
      needsOptimize: !resolved && isHighNotHelpful(item.notHelpful, item.total, rateThreshold, minNotHelpful),
      note: opt?.note ?? null,
      owner: opt?.owner ?? null,
      resolvedBy: opt?.resolved_by ?? null,
      edited: hasLiveOverride(overrides[item.qKey]),
    };
  });
}

/** 每位负责人的工作量（待优化 / 已完善）。 */
export interface OwnerWorkload {
  owner: string;
  pending: number;
  resolved: number;
  total: number;
  qKeys: string[];
}

/**
 * 按负责人汇总「待优化（未完善的高没帮助占比项）」与「已完善」的工作量，
 * 用于内容同学认领与周报。未分配负责人的项归入 unassignedLabel。
 */
export function buildOwnerSummary(stats: FaqStatRow[], unassignedLabel: string): OwnerWorkload[] {
  const map = new Map<string, OwnerWorkload>();
  const ensure = (owner: string): OwnerWorkload => {
    const existing = map.get(owner);
    if (existing) return existing;
    const created: OwnerWorkload = { owner, pending: 0, resolved: 0, total: 0, qKeys: [] };
    map.set(owner, created);
    return created;
  };
  stats.forEach((item) => {
    if (!item.needsOptimize && !item.resolved) return;
    const owner = (item.owner || item.resolvedBy || "").trim() || unassignedLabel;
    const workload = ensure(owner);
    if (item.resolved) workload.resolved += 1;
    else workload.pending += 1;
    workload.total += 1;
    if (!workload.qKeys.includes(item.qKey)) workload.qKeys.push(item.qKey);
  });
  return Array.from(map.values()).sort(
    (a, b) => b.pending - a.pending || b.resolved - a.resolved || b.total - a.total,
  );
}